import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Button, Col, Form, Grid, Input, Layout, Row, Select, Space, Spin, Tag, Typography, theme } from "antd";
import React, { useEffect, useRef, useState } from "react";
import AppHeader from "../layout/AppHeader";
import { useDispatch } from "react-redux";
import { collection, doc, getDoc, onSnapshot, query } from "firebase/firestore";
import { db, storage } from "../../config/firebase";
import { AIProvider, ChatRoom, User } from "../../models/models";
import { getUser, createChatRoom } from "../../apis/apis";
import { setPromptSendMessageData } from "../../redux/slices/promptSendMessageSlice";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { auth } from "../../config/firebase";
import { setMenuSelection } from "../../redux/slices/menuSelectionSlice";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/config";

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;
const { useToken } = theme;

const ACCEPT = ["image/*", ".pdf", ".csv", ".txt", ".docx", ".xls", ".xlsx"].join(",");
const MAX_FILES = 5;
const MAX_SIZE_MB = 20;

const getLocalizedListEntry = (source: any, language: string, index: number): string => {
  if (!source) return "";
  const languagePriority = Array.from(new Set([language, "es", "en"]));

  if (Array.isArray(source)) {
    const entry = source[index];
    if (entry && typeof entry === "object") {
      for (const lang of languagePriority) {
        const value = entry?.[lang];
        if (typeof value === "string" && value.length) return value;
      }
      const fallback = Object.values(entry).find((val) => typeof val === "string" && val.length);
      if (typeof fallback === "string") return fallback;
    } else if (typeof entry === "string" && entry.length) {
      return entry;
    }
    return "";
  }

  if (typeof source === "object") {
    for (const lang of languagePriority) {
      const arr = source?.[lang];
      if (Array.isArray(arr) && typeof arr[index] === "string" && arr[index].length) {
        return arr[index];
      }
    }
    const fallbackArr = Object.values(source).find(
      (val) => Array.isArray(val) && typeof val[index] === "string" && val[index].length,
    ) as string[] | undefined;
    return fallbackArr?.[index] ?? "";
  }

  return "";
};

function guessContentTypeFromName(name: string): string {
  const ext = (name.split(".").pop() || "").toLowerCase();
  switch (ext) {
    case "pdf": return "application/pdf";
    case "csv": return "text/csv";
    case "txt": return "text/plain";
    case "docx": return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xls": return "application/vnd.ms-excel";
    case "xlsx": return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "png": return "image/png";
    case "jpg":
    case "jpeg": return "image/jpeg";
    case "webp": return "image/webp";
    case "json": return "application/json";
    default: return "application/octet-stream";
  }
}

const PromptMasterPageContent: React.FC = () => {
  const { token } = useToken();
  const screens = useBreakpoint();
  const dispatch = useDispatch();   
  const isMobile = !screens.lg;
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [curretUser, setCurrentUser] = useState<User | null>(null);
  const [aiModels, setAiModels] = useState<any[]>([]);
  const [model, setModel] = useState<string | null>(null);

  const [promptMaster, setPromptMaster] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pmFiles, setPmFiles] = useState<File[]>([]);
  const removePmFile = (idx: number) => setPmFiles(prev => prev.filter((_, i) => i !== idx));

  const firstVarName = promptMaster?.variables?.[0];
  const firstVarLabel = getLocalizedListEntry(promptMaster?.values, i18n.language, 0) || t('prompts.taskToPerform');
  const firstVarPlaceholder = getLocalizedListEntry(promptMaster?.placeholders, i18n.language, 0);

  useEffect(() => {
    (async () => {
      const user = await getUser();
      setCurrentUser(user);
    })();
  }, []);

  useEffect(() => {
    if (!curretUser?.clientId) return;
    const providersRef = collection(db, `Clients/${curretUser.clientId}/AIProviders`);
    const q = query(providersRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const providersData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as AIProvider[];
      if (!providersData.length) return;
      const opts = providersData.map((p) => ({ value: p.name, label: p.name }));
      setAiModels(opts);
      setModel(prev => prev || opts[0]?.value || null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [curretUser]);

  useEffect(() => {
    const promptRef = doc(db, `Prompts/csnOoOImlPt4uY6djxH5`);
    (async () => {
      const snap = await getDoc(promptRef);
      if (snap.exists()) setPromptMaster({ id: snap.id, ...snap.data() });
      setLoading(false);
    })();
  }, []);

  const handleAttachClick = () => fileInputRef.current?.click();

  const onPmFilesChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = Math.max(0, MAX_FILES - pmFiles.length);
    if (!remaining) return message.warning(t('prompts.maxFiles', { max: MAX_FILES }));
    const take = files.slice(0, remaining);
    const tooBig = take.find(f => f.size / (1024 * 1024) > MAX_SIZE_MB);
    if (tooBig) return message.error(t('chat.fileExceeds', { max: MAX_SIZE_MB }));
    setPmFiles(prev => [...prev, ...take]);
  };

  const uploadSingleToChatRoom = async (file: File, chatRoomId: string) => {
    if (!auth.currentUser || !curretUser?.clientId) {
      message.error(t('prompts.noSessionOrClient'));
      return null;
    }
    const safeName = file.name.replace(/\s+/g, "_");
    const path = `Clients/${curretUser.clientId}/Users/${auth.currentUser.uid}/ChatRooms/${chatRoomId}/attachments/${Date.now()}_${safeName}`;
    const ref = storageRef(storage, path);
    const ct = file.type || guessContentTypeFromName(file.name);
    try {
      await uploadBytes(ref, file, { contentType: ct });
      const url = await getDownloadURL(ref);
      return { name: file.name, contentType: ct, size: file.size, gcsPath: path, downloadURL: url };
    } catch {
      message.error(`${t('chat.uploadError')} ${file.name}`);
      return null;
    }
  };

  const handleSend = async () => {
    if (!promptMaster) return;
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    const values = await form.validateFields();
    const textValue = String(values?.[firstVarName] ?? "").trim();
    if (!textValue && pmFiles.length === 0) {
      return message.error(t('prompts.enterOrAttachFile', { field: firstVarLabel }));
    }
    setSaving(true);
    try {
      let filledPrompt = promptMaster.prompt[i18n.language] as string;
      (promptMaster.variables || []).forEach((variable: any) => {
        const clean = String(variable).replace(/,/g, "").trim();
        const re = new RegExp(`@${clean}`, "g");
        const v = String(values?.[clean] ?? "");
        filledPrompt = filledPrompt.replace(re, v);
      });

      const baseName = promptMaster.name[i18n.language] || "Prompt Master";
      const chatName = baseName.length > 23 ? `${baseName.substring(0, 20)}...` : baseName;
      const newChat: ChatRoom = { id: "", name: chatName, userId: auth.currentUser.uid, createdAt: null };
      const chatRoomId = await createChatRoom(newChat);
      if (!chatRoomId) {
        setSaving(false);
        return message.error(t('chat.chatCreationError'));
      }

      let wireAttachments: any[] = [];
      if (pmFiles.length) {
        const uploaded = await Promise.all(pmFiles.map(f => uploadSingleToChatRoom(f, chatRoomId)));
        wireAttachments = (uploaded.filter(Boolean) as any[]);
      }

      dispatch({ type: "chatRoom/setChatRoom", payload: { id: chatRoomId, name: chatName, userId: auth.currentUser.uid, createdAt: null } });
      dispatch(setPromptSendMessageData({ promptId: promptMaster.id, prompt: filledPrompt, chatRoomId, model: model || "ChatGPT", attachments: wireAttachments }));
      dispatch(setMenuSelection("chat"));
      message.success(t('prompts.promptSentToChat'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !promptMaster) {
    return (
      <Layout style={{ backgroundColor: token.colorBgBase }}>
        <Row justify="center" align="middle" style={{ minHeight: "50vh", padding: 24 }}>
          <Col>
            <Spin size="large" />
          </Col>
        </Row>
      </Layout>
    );
  }

  return (
    <Layout style={{ display: "flex", flexDirection: "column", minHeight: "100svh", background: token.colorBgBase }}>
       <AppHeader position={isMobile ? 'fixed' : 'sticky'} />

      <Content style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Row justify="center" style={{ flex: 1, minHeight: 0, marginTop: isMobile ? 50 : 0 }}>
          <Col style={{ width: "100%", maxWidth: 900, padding: screens.xs ? 16 : 24, paddingBottom: 96 }}>
            <Row style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>{promptMaster.name[i18n.language]}</Title>
                <Paragraph type="secondary" style={{ marginTop: 8 }}>{promptMaster.description[i18n.language]}</Paragraph>
              </Col>
            </Row>

            <Form form={form} layout="vertical" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }}>
              <Form.Item
                key={firstVarName}
                label={firstVarLabel}
                name={firstVarName}
                rules={[
                  {
                    validator: async (_, _value) => {
                      const v = String(_value ?? "").trim();
                      if (v || pmFiles.length > 0) return;
                      throw new Error(t('prompts.enterOrAttachFile', { field: firstVarLabel }));
                    },
                  },
                ]}
              >
                <Row gutter={8} wrap={false} align="middle">
                  <Col flex="none">
                    <Button icon={<PlusOutlined />} onClick={handleAttachClick} />
                    <input ref={fileInputRef} type="file" accept={ACCEPT} multiple style={{ display: "none" }} onChange={onPmFilesChosen} />
                  </Col>
                  <Col flex="auto">
                    <Input placeholder={firstVarPlaceholder || firstVarLabel} />
                  </Col>
                </Row>
              </Form.Item>

              {pmFiles.length > 0 && (
                <Row gutter={[6, 6]} wrap style={{ marginTop: 8, marginBottom: 8 }}>
                  {pmFiles.map((f, idx) => (
                    <Col key={`${f.name}_${idx}`}>
                      <Tag
                        closable
                        onClose={(e) => { e.preventDefault(); removePmFile(idx); }}
                        closeIcon={<DeleteOutlined />}
                      >
                        <Space size={6} align="center">
                          <Text style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</Text>
                        </Space>
                      </Tag>
                    </Col>
                  ))}
                </Row>
              )}

              <Form.Item
                key="model"
                label={t('prompts.aiModel')}
                name="ai_model"
                rules={[{ required: true, message: t('prompts.selectAiModel') }]}
                initialValue={model || undefined}
              >
                <Select value={model ?? undefined} onChange={setModel as any} options={aiModels} placeholder={t('prompts.aiModel')} style={{ minWidth: 200 }} />
              </Form.Item>

              <Row justify="end" gutter={8} style={{ marginTop: 8 }}>
                <Col>
                  <Button type="primary" onClick={handleSend} loading={saving}>
                    {t('prompts.sendToChat')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default PromptMasterPageContent;
